import * as React from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { UserForm } from '../components/UserForm';
import { User, UserFormData } from '../types';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { UsersService } from '@/services/users-service';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';

export function EditUserPage() {
  const { id } = useParams({ from: '/_authenticated/users/edit/$id' });
  const [user, setUser] = React.useState<User | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const fetchedUser = await UsersService.getUser(id);
        setUser(fetchedUser);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user';
        setError(errorMessage);
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const handleSubmit = async (data: UserFormData) => {
    try {
      setIsSaving(true);
      const updatedUser = await UsersService.updateUser(id, data);
      setUser(updatedUser);
      toast({
        title: 'Success',
        description: 'User updated successfully',
      });
      // Navigate to the user details page
      navigate({ to: `/users/${id}` });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update user';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate({ to: `/users/${id}` });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading user...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <Alert variant="destructive">
        <Info className="h-4 w-4" />
        <AlertTitle>User Not Found</AlertTitle>
        <AlertDescription>
          {error || `The user with ID ${id} could not be found. They may have been deleted or you may not have permission to view them.`}
        </AlertDescription>
      </Alert>
    );
  }

  // Convert User to UserFormData
  const initialData: UserFormData = {
    username: user.username,
    email: user.email,
    fullName: user.fullName,
    role: user.role,
    isActive: user.isActive,
    twoFactorEnabled: user.twoFactorEnabled,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Edit User: ${user.fullName}`}
        description="Update user information and permissions."
      />
      
      <Card>
        <CardContent className="pt-6">
          <UserForm 
            initialData={initialData} 
            onSubmit={handleSubmit} 
            onCancel={handleCancel}
            isLoading={isSaving} 
            isEditMode={true}
          />
        </CardContent>
      </Card>
    </div>
  );
}
